import {
  Button,
  InlineAlert,
  Page,
  PageActions,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  TableSkeleton,
} from '@planuze/ui';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { CreateUserModal } from '../components/users/create-user-modal.js';
import { UsersFilters, type VerifiedFilter } from '../components/users/users-filters.js';
import { UsersTable } from '../components/users/users-table.js';
import { useUsersListContent } from '../content/sections/users-list.js';
import { useDebounce } from '../hooks/use-debounce.js';
import { useStaffSession } from '../hooks/use-staff-session.js';
import { useUsersList } from '../hooks/use-users.js';

export function UsersListPage() {
  const navigate = useNavigate();
  const session = useStaffSession();
  const copy = useUsersListContent();
  /* Criar conta comum é admin-created (ADR-0710) — só super_admin (espelha ADR-0421). */
  const canCreate = session?.role === 'super_admin';
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [verified, setVerified] = useState<VerifiedFilter>('all');
  const debouncedSearch = useDebounce(search, 300);
  const hasSearch = debouncedSearch.length >= 2;
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useUsersList(
    {
      search: hasSearch ? debouncedSearch : undefined,
      verified: verified === 'all' ? undefined : verified,
    },
  );
  const items = data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.title}</PageTitle>
          <PageDescription>{copy.description}</PageDescription>
        </PageTitleBlock>
        {canCreate && (
          <PageActions>
            <Button onClick={() => setCreateOpen(true)}>{copy.createUser}</Button>
          </PageActions>
        )}
      </PageHeader>
      <PageContent className="space-y-4">
        <UsersFilters
          value={search}
          onChange={setSearch}
          verified={verified}
          onVerifiedChange={setVerified}
        />
        {isError ? (
          <InlineAlert variant="destructive">{copy.loadFailed}</InlineAlert>
        ) : isLoading ? (
          <TableSkeleton columns={3} />
        ) : (
          <UsersTable
            items={items}
            hasFilters={hasSearch || verified !== 'all'}
            onClearFilters={() => {
              setSearch('');
              setVerified('all');
            }}
            onRowClick={(id) => {
              navigate({ to: '/users/$id', params: { id } }).catch(() => undefined);
            }}
          />
        )}
        {hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              loading={isFetchingNextPage}
              onClick={() => {
                fetchNextPage().catch(() => undefined);
              }}
            >
              {copy.loadMore}
            </Button>
          </div>
        )}
      </PageContent>
      <CreateUserModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </Page>
  );
}
